import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Pagination,
  CircularProgress,
  Menu,
  MenuItem,
  ListSubheader,
  Checkbox,
  ListItemText,
  Tooltip,
  Alert,
  Select,
  Divider,
} from "@mui/material";
import {
  Search,
  Save,
  Undo,
  ViewColumn,
  GridOn,
  Refresh,
} from "@mui/icons-material";
import api from "../../api";
import PageShell, { PanelCard } from "../../components/common/PageShell";
import DataTableFrame from "../../components/common/DataTableFrame";
import { calculateAkkClass } from "../../utils/akkUtils";
import { calculateStorieIndex } from "../../utils/storieUtils";
import { SEKMELER, gruplariDuzles, duzSecenek } from "./bulkEditColumns";

/**
 * TOPLU DÜZENLEME
 *
 * "Tek tıkla hepsini değiştir" DEĞİLDİR. Amaç, profil ve sondalara ait TÜM
 * verinin — katmanlar (horizon) dahil — tek bir ekrandan hücre hücre
 * düzenlenebilmesidir.
 *
 * Dört sekme vardır: Profiller, Profil Katmanları, Sondalar, Sonda Katmanları.
 * Katman sekmelerinde her horizon kendi satırı olur; hangi profile/sondaya ait
 * olduğu sabit sütunda görünür.
 *
 * Seçenek listesi olan alanlar açılır liste olarak gelir (formun kendisiyle
 * aynı listeler), böylece elle yazımdan kaynaklı bozuk veri oluşmaz.
 * Değişen hücreler işaretlenir; yalnızca değişen kayıtlar sunucuya gider.
 */

const SAYFA_BOYUTU = 50;

const guvenliParse = (veri) => {
  if (!veri) return {};
  if (typeof veri === "object") return veri;
  try {
    return JSON.parse(veri);
  } catch (e) {
    return {};
  }
};

const bosDegil = (v) => v !== undefined && v !== null && v !== "";

export default function BulkEditPage() {
  const [sekme, setSekme] = useState("profil");
  const [kayitlar, setKayitlar] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [sayfaSayisi, setSayfaSayisi] = useState(1);
  const [sayfa, setSayfa] = useState(1);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const [arama, setArama] = useState("");
  const [gecikmisArama, setGecikmisArama] = useState("");

  // { satirId: { alanAdi: yeniDeger } }  (katmanlarda satirId = "12#0")
  const [degisiklikler, setDegisiklikler] = useState({});

  const [seciliSutunlar, setSeciliSutunlar] = useState(
    SEKMELER.profil.varsayilan,
  );
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [sutunArama, setSutunArama] = useState("");

  const ayar = SEKMELER[sekme];
  const profilTarafi = sekme === "profil" || sekme === "profilKatman";
  const katmanModu = ayar.katman;

  const tumSutunlar = useMemo(() => gruplariDuzles(ayar.gruplar), [ayar]);
  const sutunlar = useMemo(
    () => tumSutunlar.filter((c) => seciliSutunlar.includes(c.key)),
    [tumSutunlar, seciliSutunlar],
  );

  const degisiklikSayisi = Object.keys(degisiklikler).length;

  useEffect(() => {
    const t = setTimeout(() => {
      setGecikmisArama(arama);
      setSayfa(1);
    }, 400);
    return () => clearTimeout(t);
  }, [arama]);

  const kayitlariGetir = useCallback(async () => {
    setYukleniyor(true);
    try {
      const uc = profilTarafi ? "/admin/all-data" : "/admin/all-sonda-data";
      const res = await api.get(uc, {
        params: {
          page: sayfa,
          limit: SAYFA_BOYUTU,
          search: gecikmisArama,
          sortBy: profilTarafi ? "profil_no" : "sonda_no",
          sortDir: "asc",
        },
      });
      if (res.data.success) {
        setKayitlar(res.data.data || []);
        setToplam(res.data.total || 0);
        setSayfaSayisi(res.data.pageCount || 1);
      }
    } catch (err) {
      console.error("Kayıtlar çekilemedi:", err);
    } finally {
      setYukleniyor(false);
    }
  }, [profilTarafi, sayfa, gecikmisArama]);

  useEffect(() => {
    kayitlariGetir();
  }, [kayitlariGetir]);

  // Ekranda gosterilecek satirlar. Katman sekmelerinde her horizon ayri satir.
  const satirlar = useMemo(() => {
    if (!katmanModu)
      return kayitlar.map((k) => ({
        satirId: String(k.id),
        kayit: k,
        ustNo: profilTarafi ? k.profil_no : k.sonda_no,
      }));

    const cikti = [];
    kayitlar.forEach((k) => {
      const veri = guvenliParse(k.full_data);
      const katmanlar = Array.isArray(veri.horizons) ? veri.horizons : [];
      katmanlar.forEach((h, i) => {
        cikti.push({
          satirId: `${k.id}#${i}`,
          kayit: k,
          katmanIndex: i,
          katman: h || {},
          ustNo: profilTarafi ? k.profil_no : k.sonda_no,
        });
      });
    });
    return cikti;
  }, [kayitlar, katmanModu, profilTarafi]);

  const sekmeDegistir = (yeniSekme) => {
    if (degisiklikSayisi > 0) {
      if (
        !window.confirm(
          `${degisiklikSayisi} satırda kaydedilmemiş değişiklik var. Yine de geçmek istiyor musunuz?`,
        )
      )
        return;
    }
    setDegisiklikler({});
    setSekme(yeniSekme);
    setSayfa(1);
    setSutunArama("");
    setSeciliSutunlar(SEKMELER[yeniSekme].varsayilan);
  };

  // --- hucre okuma / yazma ---------------------------------------------------
  const orijinalDeger = (satir, sutun) => {
    if (katmanModu) return satir.katman[sutun.key] ?? "";
    if (sutun.ustSeviye) return satir.kayit[sutun.key] ?? "";
    return guvenliParse(satir.kayit.full_data)[sutun.key] ?? "";
  };

  const hucreDegeri = (satir, sutun) => {
    const d = degisiklikler[satir.satirId];
    if (d && d[sutun.key] !== undefined) return d[sutun.key];
    return orijinalDeger(satir, sutun);
  };

  const ayniMi = (a, b) => {
    if (Array.isArray(a) || Array.isArray(b))
      return JSON.stringify(a || []) === JSON.stringify(b || []);
    return String(a ?? "") === String(b ?? "");
  };

  const hucreDegisti = (satir, sutun, deger) => {
    setDegisiklikler((onceki) => {
      const yeni = { ...onceki };
      const satirDeg = { ...(yeni[satir.satirId] || {}) };

      if (ayniMi(deger, orijinalDeger(satir, sutun))) delete satirDeg[sutun.key];
      else satirDeg[sutun.key] = deger;

      if (Object.keys(satirDeg).length === 0) delete yeni[satir.satirId];
      else yeni[satir.satirId] = satirDeg;
      return yeni;
    });
  };

  const degistiMi = (satir, sutun) =>
    degisiklikler[satir.satirId]?.[sutun.key] !== undefined;

  const vazgec = () => {
    if (!window.confirm("Tüm değişiklikler geri alınsın mı?")) return;
    setDegisiklikler({});
  };

  // --- kaydetme --------------------------------------------------------------
  const kaydet = async () => {
    if (degisiklikSayisi === 0) return;

    // Katman satirlari ayni kayda ait olabilir; kac KAYIT guncellenecegini say.
    const etkilenenKayitlar = new Set(
      Object.keys(degisiklikler).map((id) => id.split("#")[0]),
    );
    if (
      !window.confirm(
        `${degisiklikSayisi} satırdaki değişiklik ${etkilenenKayitlar.size} kayda yazılacak. Onaylıyor musunuz?`,
      )
    )
      return;

    setKaydediliyor(true);
    try {
      // kayitId -> { fullData, ustNo }
      const birikim = {};
      const kayitBul = (id) => kayitlar.find((k) => String(k.id) === id);

      const birikimAl = (kayitId) => {
        if (!birikim[kayitId]) {
          const kayit = kayitBul(kayitId);
          if (!kayit) return null;
          birikim[kayitId] = {
            kayit,
            fullData: { ...guvenliParse(kayit.full_data) },
            ustNo: profilTarafi ? kayit.profil_no : kayit.sonda_no,
          };
        }
        return birikim[kayitId];
      };

      Object.entries(degisiklikler).forEach(([satirId, alanlar]) => {
        const [kayitId, katmanIndexStr] = satirId.split("#");
        const hedef = birikimAl(kayitId);
        if (!hedef) return;

        if (katmanModu) {
          const i = Number(katmanIndexStr);
          const katmanlar = Array.isArray(hedef.fullData.horizons)
            ? [...hedef.fullData.horizons]
            : [];
          katmanlar[i] = { ...(katmanlar[i] || {}), ...alanlar };
          hedef.fullData.horizons = katmanlar;
        } else {
          Object.entries(alanlar).forEach(([alan, deger]) => {
            const sutun = tumSutunlar.find((c) => c.key === alan);
            if (sutun?.ustSeviye) hedef.ustNo = deger;
            hedef.fullData[alan] = deger;
          });
        }
      });

      const paket = Object.values(birikim).map((h) => {
        if (profilTarafi) {
          // AKK ve indeks turetilmis degerler; katman (tekstur/kireç) veya
          // profil alanlari degisince yeniden hesaplanmalari gerekir.
          let akk = h.kayit.akk_sonuc || "";
          let indeks = h.kayit.storie_index || 0;
          try {
            const a = calculateAkkClass(h.fullData);
            akk = `${a.finalClass}${a.subClassString}`;
          } catch (e) {}
          try {
            const st = calculateStorieIndex(h.fullData);
            if (st.finalIndex !== "NaN") indeks = parseFloat(st.finalIndex);
          } catch (e) {}

          h.fullData.profil_no = h.ustNo;
          return {
            id: h.kayit.id,
            profil_no: h.ustNo,
            full_data: h.fullData,
            akk_sonuc: akk,
            storie_index: indeks,
          };
        }
        h.fullData.sonda_no = h.ustNo;
        return {
          id: h.kayit.id,
          sonda_no: h.ustNo,
          full_data: h.fullData,
        };
      });

      if (profilTarafi)
        await api.put("/admin/update-bulk-profiles", { profiles: paket });
      else await api.put("/admin/update-bulk-sondas", { sondas: paket });

      alert(`✅ ${paket.length} kayıt güncellendi.`);
      setDegisiklikler({});
      kayitlariGetir();
    } catch (err) {
      console.error(err);
      alert(
        "❌ Kaydetme başarısız: " +
          (err.response?.data?.message || "Sunucu hatası"),
      );
    } finally {
      setKaydediliyor(false);
    }
  };

  // --- sutun secim menusu ----------------------------------------------------
  const sutunAcKapa = (key) =>
    setSeciliSutunlar((onceki) =>
      onceki.includes(key)
        ? onceki.filter((k) => k !== key)
        : [...onceki, key],
    );

  const grupAcKapa = (grup) => {
    const anahtarlar = grup.alanlar.map((a) => a.key);
    const hepsiVar = anahtarlar.every((k) => seciliSutunlar.includes(k));
    setSeciliSutunlar((onceki) =>
      hepsiVar
        ? onceki.filter((k) => !anahtarlar.includes(k))
        : [...new Set([...onceki, ...anahtarlar])],
    );
  };

  const filtreliGruplar = useMemo(() => {
    const q = sutunArama.trim().toLocaleLowerCase("tr-TR");
    if (!q) return ayar.gruplar;
    return ayar.gruplar
      .map((g) => ({
        ...g,
        alanlar: g.alanlar.filter((a) =>
          a.label.toLocaleLowerCase("tr-TR").includes(q),
        ),
      }))
      .filter((g) => g.alanlar.length > 0);
  }, [ayar, sutunArama]);

  // --- hucre bileseni --------------------------------------------------------
  const hucreCiz = (satir, sutun) => {
    const degisti = degistiMi(satir, sutun);
    const deger = hucreDegeri(satir, sutun);

    const ortakSx = {
      "& .MuiInputBase-input, & .MuiSelect-select": {
        fontSize: "0.78rem",
        px: 0.75,
        py: 0.5,
        bgcolor: degisti ? "#fff3cd" : "transparent",
        fontWeight: degisti ? 700 : 400,
        borderRadius: 1,
      },
    };

    if (sutun.tip === "onay")
      return (
        <Checkbox
          size="small"
          checked={Boolean(deger)}
          onChange={(e) => hucreDegisti(satir, sutun, e.target.checked)}
          sx={{ bgcolor: degisti ? "#fff3cd" : "transparent", borderRadius: 1 }}
        />
      );

    if (sutun.secenekler) {
      const liste = duzSecenek(sutun.secenekler);
      const mevcut = sutun.cokSecim
        ? Array.isArray(deger)
          ? deger
          : bosDegil(deger)
            ? [deger]
            : []
        : (deger ?? "");

      // Kayitli deger listede yoksa kaybolmasin diye listeye ekleniyor.
      const tamListe = [...liste];
      const ekle = (v) => {
        if (bosDegil(v) && !tamListe.includes(v)) tamListe.push(v);
      };
      if (sutun.cokSecim) mevcut.forEach(ekle);
      else ekle(mevcut);

      return (
        <Select
          variant="standard"
          size="small"
          fullWidth
          multiple={Boolean(sutun.cokSecim)}
          displayEmpty
          value={mevcut}
          onChange={(e) => hucreDegisti(satir, sutun, e.target.value)}
          disableUnderline={!degisti}
          renderValue={(v) =>
            Array.isArray(v) ? v.join(", ") : v || <em>—</em>
          }
          MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          sx={ortakSx}
        >
          {!sutun.cokSecim && (
            <MenuItem value="">
              <em>— boş —</em>
            </MenuItem>
          )}
          {tamListe.map((s) => (
            <MenuItem key={s} value={s} dense>
              {sutun.cokSecim && (
                <Checkbox
                  size="small"
                  checked={mevcut.includes(s)}
                  sx={{ p: 0.25, mr: 0.75 }}
                />
              )}
              <ListItemText
                primary={s}
                primaryTypographyProps={{ fontSize: "0.8rem" }}
              />
            </MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <TextField
        variant="standard"
        size="small"
        fullWidth
        multiline={sutun.tip === "uzun"}
        maxRows={sutun.tip === "uzun" ? 4 : undefined}
        type={
          sutun.tip === "sayi"
            ? "number"
            : sutun.tip === "tarih"
              ? "date"
              : "text"
        }
        value={deger ?? ""}
        onChange={(e) => hucreDegisti(satir, sutun, e.target.value)}
        InputProps={{ disableUnderline: !degisti }}
        InputLabelProps={sutun.tip === "tarih" ? { shrink: true } : undefined}
        sx={ortakSx}
      />
    );
  };

  const ustBaslik = profilTarafi ? "Profil No" : "Sonda No";

  return (
    <PageShell
      title="Toplu Düzenleme"
      subtitle="Profil, sonda ve katmanlara ait tüm verileri tek ekrandan hücre hücre düzenleyin."
      icon={<GridOn />}
      chip={
        <Chip
          label={
            katmanModu
              ? `${satirlar.length} Katman / ${toplam} Kayıt`
              : `${toplam} Kayıt`
          }
          size="small"
          color="primary"
          variant="outlined"
        />
      }
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={kayitlariGetir}
            disabled={yukleniyor || kaydediliyor}
          >
            Yenile
          </Button>
          <Button
            size="small"
            startIcon={<Undo />}
            onClick={vazgec}
            disabled={degisiklikSayisi === 0 || kaydediliyor}
          >
            Vazgeç
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={
              kaydediliyor ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Save />
              )
            }
            onClick={kaydet}
            disabled={degisiklikSayisi === 0 || kaydediliyor}
          >
            Kaydet ({degisiklikSayisi})
          </Button>
        </Stack>
      }
    >
      <PanelCard sx={{ mb: 2 }}>
        <Tabs
          value={sekme}
          onChange={(e, v) => sekmeDegistir(v)}
          sx={{ mb: 2, minHeight: 40 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {Object.entries(SEKMELER).map(([anahtar, s]) => (
            <Tab
              key={anahtar}
              value={anahtar}
              label={s.etiket}
              sx={{ minHeight: 40, textTransform: "none", fontWeight: 700 }}
            />
          ))}
        </Tabs>

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder={
              profilTarafi ? "Profil no / seri ara..." : "Sonda no ara..."
            }
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumn />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            Sütunlar ({sutunlar.length}/{tumSutunlar.length})
          </Button>
          <Typography variant="caption" color="text.secondary">
            {katmanModu
              ? "Her satır bir katmandır; aynı kaydın katmanları tek kaydedişte birlikte yazılır."
              : "Seçenek listesi olan alanlar açılır liste gelir; elle yazım hatası olmaz."}
          </Typography>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => {
            setMenuAnchor(null);
            setSutunArama("");
          }}
          slotProps={{ paper: { sx: { maxHeight: 480, width: 330 } } }}
        >
          <Box sx={{ px: 1.5, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Alan ara..."
              value={sutunArama}
              onChange={(e) => setSutunArama(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={() =>
                  setSeciliSutunlar(tumSutunlar.map((c) => c.key))
                }
              >
                Tümünü seç
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => setSeciliSutunlar(ayar.varsayilan)}
              >
                Varsayılan
              </Button>
            </Stack>
          </Box>
          <Divider />

          {filtreliGruplar.map((g) => [
            <ListSubheader
              key={`${g.grup}-baslik`}
              onClick={() => grupAcKapa(g)}
              sx={{
                lineHeight: "30px",
                fontSize: "0.72rem",
                fontWeight: 800,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              {g.grup.toLocaleUpperCase("tr-TR")}
            </ListSubheader>,
            ...g.alanlar.map((c) => (
              <MenuItem key={c.key} dense onClick={() => sutunAcKapa(c.key)}>
                <Checkbox
                  size="small"
                  checked={seciliSutunlar.includes(c.key)}
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemText
                  primary={c.label}
                  primaryTypographyProps={{ fontSize: "0.82rem" }}
                />
              </MenuItem>
            )),
          ])}
        </Menu>

        {degisiklikSayisi > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <b>{degisiklikSayisi}</b> satırda kaydedilmemiş değişiklik var.
            Sayfa veya sekme değiştirirseniz bu değişiklikler kaybolur.
          </Alert>
        )}
      </PanelCard>

      <DataTableFrame
        minWidth={sutunlar.reduce((t, c) => t + (c.genislik || 140), 0) + 220}
        size="small"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 56, textAlign: "center" }}>#</TableCell>
            {/* Ust kayit sutunu yalnizca katman sekmelerinde gerekli: satirin
                hangi profile/sondaya ait oldugunu gosterir. Normal sekmelerde
                numara zaten duzenlenebilir sutun olarak var. */}
            {katmanModu && (
              <TableCell sx={{ width: 110 }}>{ustBaslik}</TableCell>
            )}
            {sutunlar.map((c) => (
              <TableCell key={c.key} sx={{ minWidth: c.genislik }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {yukleniyor && (
            <TableRow>
              <TableCell colSpan={sutunlar.length + (katmanModu ? 2 : 1)} align="center">
                <CircularProgress size={24} sx={{ my: 2 }} />
              </TableCell>
            </TableRow>
          )}

          {!yukleniyor && satirlar.length === 0 && (
            <TableRow>
              <TableCell colSpan={sutunlar.length + (katmanModu ? 2 : 1)} align="center">
                <Typography variant="body2" color="text.secondary" py={2}>
                  {katmanModu
                    ? "Bu sayfadaki kayıtlara ait katman bulunamadı."
                    : "Kayıt bulunamadı."}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!yukleniyor &&
            satirlar.map((satir, idx) => {
              const satirDegisti = Boolean(degisiklikler[satir.satirId]);
              return (
                <TableRow
                  key={satir.satirId}
                  hover
                  sx={{ bgcolor: satirDegisti ? "#fffbf2" : "inherit" }}
                >
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontWeight: 700,
                      color: "text.secondary",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {idx + 1}
                  </TableCell>

                  {katmanModu && (
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        bgcolor: "#fafafa",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {satir.ustNo || "—"}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        katman {satir.katmanIndex + 1}
                      </Typography>
                    </TableCell>
                  )}

                  {sutunlar.map((c) => (
                    <TableCell key={c.key} sx={{ p: 0.25 }}>
                      <Tooltip
                        title={
                          degistiMi(satir, c)
                            ? `Eski değer: ${
                                Array.isArray(orijinalDeger(satir, c))
                                  ? orijinalDeger(satir, c).join(", ") ||
                                    "(boş)"
                                  : orijinalDeger(satir, c) || "(boş)"
                              }`
                            : ""
                        }
                        placement="top"
                      >
                        <Box>{hucreCiz(satir, c)}</Box>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
        </TableBody>
      </DataTableFrame>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2 }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          Sayfa {sayfa} / {sayfaSayisi} — Kayıt{" "}
          {toplam === 0 ? 0 : (sayfa - 1) * SAYFA_BOYUTU + 1}–
          {Math.min(sayfa * SAYFA_BOYUTU, toplam)} / {toplam}
          {katmanModu && ` (bu sayfada ${satirlar.length} katman)`}
        </Typography>
        <Pagination
          count={sayfaSayisi}
          page={sayfa}
          onChange={(e, v) => {
            if (degisiklikSayisi > 0) {
              if (
                !window.confirm(
                  "Kaydedilmemiş değişiklikler var. Sayfa değişirse kaybolur. Devam?",
                )
              )
                return;
              setDegisiklikler({});
            }
            setSayfa(v);
          }}
          color="primary"
          shape="rounded"
        />
      </Stack>
    </PageShell>
  );
}
